/**
 * Cross-tab call ownership. Every tab resyncs the active call on mount and on
 * visibilitychange — without a guard each focus switch would "steal" the live
 * call into the focused tab (same LiveKit identity → the previous tab gets
 * kicked with DUPLICATE_IDENTITY, prompting for the mic again). The tab whose
 * Room is live answers ownership pings; resync joins only when nobody answers
 * (page reload, owner tab closed or crashed).
 */

type OwnershipMessage = { type: 'who-owns' } | { type: 'owned'; callId: string };

const OWNER_REPLY_TIMEOUT_MS = 250;

let channel: BroadcastChannel | null = null;
let ownedCallId: string | null = null;

const getChannel = (): BroadcastChannel | null => {
  if (typeof BroadcastChannel === 'undefined') {
    return null;
  }
  if (!channel) {
    channel = new BroadcastChannel('tp-call-ownership');
    channel.addEventListener('message', (event: MessageEvent<OwnershipMessage>) => {
      if (event.data?.type === 'who-owns' && ownedCallId) {
        channel?.postMessage({ type: 'owned', callId: ownedCallId } satisfies OwnershipMessage);
      }
    });
  }
  return channel;
};

/** Mark (or clear) this tab as the one holding the live room connection. */
export const setOwnedCall = (callId: string | null): void => {
  ownedCallId = callId;
  getChannel();
};

/** Resolves true when another tab currently holds the live room for this call. */
export const isCallOwnedElsewhere = (callId: string): Promise<boolean> => {
  const ch = getChannel();
  if (!ch) {
    return Promise.resolve(false);
  }
  return new Promise((resolve) => {
    const finish = (owned: boolean) => {
      window.clearTimeout(timer);
      ch.removeEventListener('message', onMessage);
      resolve(owned);
    };
    const onMessage = (event: MessageEvent<OwnershipMessage>) => {
      if (event.data?.type === 'owned' && event.data.callId === callId) {
        finish(true);
      }
    };
    const timer = window.setTimeout(() => finish(false), OWNER_REPLY_TIMEOUT_MS);
    ch.addEventListener('message', onMessage);
    ch.postMessage({ type: 'who-owns' } satisfies OwnershipMessage);
  });
};
